<script setup>
/**
 * 链编辑器 —— 一条有序链的可视化编辑：拖拽换位、上移下移、增删步骤、每一步从「已启用的 (厂商, 模型)」里选。
 *
 * **只管编辑，不管保存**：链从 `steps` 进来，改完从 `update:steps` 出去。谁来存、存到哪个 mode、被后端
 * 拒了怎么办，由 `Modes.vue` 处理整条链的保存结果。
 * 所有换位/增删都委托给 `../mode-chain.js` 的纯函数 —— 拖拽和箭头是同一条规则的两个入口，不是两套实现。
 *
 * 拖拽用原生 HTML5 draggable。箭头按钮不是冗余：只能拖的界面对键盘用户等于不能用。
 *
 * 页面上不写规则，规则就是按钮的状态：
 *   - 只剩一步时删除键 `disabled` —— 空链会让每个游戏请求炸，所以 UI 里根本删不空。
 *   - 没有兜底（≤ 1 步）时「加一步」是主按钮，有兜底后退回次要按钮。
 */
import { computed, ref } from 'vue'
import { ArrowDown, ArrowUp, GripVertical, Plus, Trash2 } from '@lucide/vue'
import { Badge, Button, Select } from '@drama/ui'
import { useI18n } from 'vue-i18n'
import { moveStep, newStep, removeStep, stepFromKey, stepKey, stepRole } from '../mode-chain.js'

const props = defineProps({
  /** @type {import('vue').PropType<import('../mode-chain.js').EditableStep[]>} 有序，索引 0 是主力。 */
  steps: { type: Array, required: true },
  /** @type {import('vue').PropType<import('../mode-chain.js').ChainStep[]>} 可选的 (厂商, 模型) 全集。 */
  options: { type: Array, required: true },
  /** @type {import('vue').PropType<Record<number, import('../mode-chain.js').StepError>>} 逐行错误码。 */
  stepErrors: { type: Object, required: true },
})

const emit = defineEmits(['update:steps'])

const { t } = useI18n()

/** 正在被拖动的那一行的索引；null = 没在拖。 */
const draggingIndex = ref(null)
/** 悬停在哪一行上方（画落点线用）。 */
const dropIndex = ref(null)

const selectOptions = computed(() =>
  props.options.map((option) => ({ value: stepKey(option), label: `${option.provider} / ${option.model}` })),
)

/** 只剩一步就不许再删 —— 空链保存下去后端会拒，别让人走到那一步。 */
const lastStepLeft = computed(() => props.steps.length === 1)
/** 没有兜底：加一步是这条链现在最该做的事，所以是主按钮。 */
const noFallback = computed(() => props.steps.length <= 1)

/** @param {number} index */
function roleLabel(index) {
  const role = stepRole(index)
  return role.role === 'primary'
    ? t('visualGameAdmin.llm.modes.chain.primary')
    : t('visualGameAdmin.llm.modes.chain.fallback', { order: role.order })
}

/** @param {number} index @param {string} key */
function pickStep(index, key) {
  const next = [...props.steps]
  next[index] = { ...next[index], ...stepFromKey(key) }
  emit('update:steps', next)
}

/** @param {number} from @param {number} to */
function move(from, to) {
  emit('update:steps', moveStep(props.steps, from, to))
}

/** @param {number} index */
function drop(index) {
  emit('update:steps', removeStep(props.steps, index))
}

function add() {
  emit('update:steps', [...props.steps, newStep()])
}

/** @param {number} index */
function onDragStart(index) {
  draggingIndex.value = index
}

/** @param {number} index */
function onDragOver(index) {
  if (draggingIndex.value === null) return
  dropIndex.value = index
}

/** @param {number} index */
function onDrop(index) {
  if (draggingIndex.value === null) throw new Error('ChainEditor.onDrop: drop without a dragging step')
  if (draggingIndex.value !== index) move(draggingIndex.value, index)
  draggingIndex.value = null
  dropIndex.value = null
}

function onDragEnd() {
  draggingIndex.value = null
  dropIndex.value = null
}
</script>

<template>
  <div
    class="chain"
    data-insp-path="apps/visual-game-admin/src/pages/llm/mode/ChainEditor.vue#chain-editor"
  >
    <ol class="chain__steps">
      <li
        v-for="(step, index) in steps"
        :key="step.uid"
        class="chain__step"
        :class="{
          'is-dragging': draggingIndex === index,
          'is-drop-target': dropIndex === index && draggingIndex !== index,
        }"
        :data-step-index="index"
        draggable="true"
        @dragstart="onDragStart(index)"
        @dragover.prevent="onDragOver(index)"
        @drop.prevent="onDrop(index)"
        @dragend="onDragEnd"
      >
        <span class="chain__grip">
          <GripVertical :size="16" />
        </span>

        <Badge :variant="index === 0 ? 'info' : 'neutral'">
          {{ roleLabel(index) }}
        </Badge>

        <div class="chain__pick">
          <Select
            :model-value="step.provider === '' ? '' : stepKey(step)"
            :options="selectOptions"
            :placeholder="t('visualGameAdmin.llm.modes.chain.placeholder')"
            :invalid="Boolean(stepErrors[index])"
            :aria-label="`${t('visualGameAdmin.llm.modes.chain.step')} ${index + 1}`"
            @update:model-value="pickStep(index, $event)"
          />
          <p
            v-if="stepErrors[index]"
            class="vga-error chain__step-error"
          >
            {{ t(`visualGameAdmin.llm.modes.errors.${stepErrors[index]}`) }}
          </p>
        </div>

        <div class="chain__step-actions">
          <Button
            variant="ghost"
            size="sm"
            :disabled="index === 0"
            :aria-label="t('visualGameAdmin.llm.modes.chain.up')"
            :title="t('visualGameAdmin.llm.modes.chain.up')"
            @click="move(index, index - 1)"
          >
            <ArrowUp :size="16" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            :disabled="index === steps.length - 1"
            :aria-label="t('visualGameAdmin.llm.modes.chain.down')"
            :title="t('visualGameAdmin.llm.modes.chain.down')"
            @click="move(index, index + 1)"
          >
            <ArrowDown :size="16" />
          </Button>
          <!-- disabled 的 button 自己不响应 hover，tooltip 挂在外面这层才看得见「至少留一步」。 -->
          <span
            :title="lastStepLeft
              ? t('visualGameAdmin.llm.modes.chain.keepOne')
              : t('visualGameAdmin.llm.modes.chain.remove')"
          >
            <Button
              variant="ghost"
              size="sm"
              :disabled="lastStepLeft"
              :aria-label="t('visualGameAdmin.llm.modes.chain.remove')"
              @click="drop(index)"
            >
              <Trash2 :size="16" />
            </Button>
          </span>
        </div>
      </li>
    </ol>

    <div class="chain__footer">
      <Button
        :variant="noFallback ? 'primary' : 'secondary'"
        size="sm"
        @click="add"
      >
        <Plus :size="16" />
        {{ t('visualGameAdmin.llm.modes.chain.add') }}
      </Button>
    </div>
  </div>
</template>

<style scoped>
.chain {
  display: grid;
  gap: var(--drama-space-lg);
}

.chain__steps {
  display: grid;
  gap: var(--drama-space-sm);
  margin: 0;
  padding: 0;
  list-style: none;
}

.chain__step {
  display: grid;
  grid-template-columns: auto auto minmax(0, 1fr) auto;
  align-items: center;
  gap: var(--drama-space-md);
  padding: var(--drama-space-md);
  border-radius: var(--drama-radius-md);
  background: var(--drama-color-surface-alt);
}

.chain__step.is-dragging {
  opacity: 0.5;
}

.chain__step.is-drop-target {
  box-shadow: inset 0 2px 0 0 var(--drama-color-primary);
}

.chain__grip {
  display: grid;
  place-items: center;
  color: var(--drama-color-text-muted);
  cursor: grab;
}

.chain__pick {
  min-width: 0;
  display: grid;
  gap: var(--drama-space-xs);
}

.chain__step-error {
  margin: 0;
  font-size: var(--drama-font-size-xs);
}

.chain__step-actions {
  display: flex;
  align-items: center;
  gap: var(--drama-space-xs);
}

.chain__footer {
  display: flex;
}
</style>
